import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TDurationUnit } from './duration-utilities.interface';

@Injectable({
  providedIn: 'root',
})
export class DurationUtilitiesService {
  public readonly MILLISECONDS_IN_A_SECOND = 1000;

  constructor(private readonly translate: TranslateService) {}

  /**
   * Formats a duration as `<hours>h <minutes>m`, e.g. "1h 15m".
   *
   * `unit` accepts a coarser 'minutes'/'hours' precision (the original behaviour, unchanged) as
   * well as a finer 'seconds'/'milliseconds' precision - values passed with the latter also show
   * a seconds component whenever the duration is under an hour (e.g. "7m 32s", "56s"), since
   * losing that precision to a bare "7m" would be misleading for values that were measured to
   * the second in the first place.
   */
  public getDuration(value: number | null, unit: TDurationUnit = 'minutes', applyMinutePadding = false): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const totalMinutes = this.convertToMinutes(value, unit);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);

    const hourSuffix = this.translate.instant('shared.timeSuffix.hour');
    const minuteSuffix = this.translate.instant('shared.timeSuffix.minute');
    const formattedMinutes = applyMinutePadding ? minutes.toString().padStart(2, '0') : minutes.toString();
    const hasSubMinutePrecision = unit === 'seconds' || unit === 'milliseconds';

    if (hours === 0 && hasSubMinutePrecision) {
      const seconds = Math.floor(this.convertToSeconds(value, unit) % 60);
      const secondSuffix = this.translate.instant('shared.timeSuffix.second');

      if (minutes === 0) {
        return `${seconds}${secondSuffix}`;
      }

      if (seconds === 0) {
        return `${formattedMinutes}${minuteSuffix}`;
      }

      return `${formattedMinutes}${minuteSuffix} ${seconds}${secondSuffix}`;
    }

    if (hours === 0) {
      return `${formattedMinutes}${minuteSuffix}`;
    }

    if (minutes === 0) {
      return `${hours}${hourSuffix}`;
    }

    return `${hours}${hourSuffix} ${formattedMinutes}${minuteSuffix}`;
  }

  /** Converts a duration expressed in `unit` to (fractional) minutes. */
  private convertToMinutes(value: number, unit: TDurationUnit): number {
    switch (unit) {
      case 'milliseconds':
        return value / this.MILLISECONDS_IN_A_SECOND / 60;
      case 'seconds':
        return value / 60;
      case 'hours':
        return value * 60;
      case 'minutes':
      default:
        return value;
    }
  }

  /** Converts a duration expressed in `unit` to (fractional) seconds. */
  private convertToSeconds(value: number, unit: TDurationUnit): number {
    switch (unit) {
      case 'milliseconds':
        return value / this.MILLISECONDS_IN_A_SECOND;
      case 'seconds':
        return value;
      case 'hours':
        return value * 3600;
      case 'minutes':
      default:
        return value * 60;
    }
  }

  public static convertVideoTimeToSeconds(videoTime: string): number {
    const parts: number[] = videoTime.split(':').map(Number);
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (parts.length === 1) {
      [seconds] = parts;
    } else if (parts.length === 2) {
      [minutes, seconds] = parts;
    } else if (parts.length === 3) {
      [hours, minutes, seconds] = parts;
    } else {
      return 0;
    }

    return hours * 3600 + minutes * 60 + seconds;
  }

  public static convertSecondsToVideoTime(totalSeconds: number, applySecondsPadding = false): string {
    if (isNaN(totalSeconds) || totalSeconds < 0) {
      return '0';
    }

    const hours: number = Math.floor(totalSeconds / 3600);
    const minutes: number = Math.floor((totalSeconds % 3600) / 60);
    const seconds: number = Math.floor(totalSeconds % 60);

    const hoursStr = String(hours);
    const minutesStr: string = String(minutes).padStart(hours > 0 ? 2 : 1, '0');
    const secondsStr: string = String(seconds).padStart(
      minutes > 0 || hours > 0 ? 2 : applySecondsPadding ? 2 : 1,
      '0',
    );

    if (hours > 0) {
      return `${hoursStr}:${minutesStr}:${secondsStr}`;
    } else if (minutes > 0) {
      return `${minutesStr}:${secondsStr}`;
    } else {
      return `${applySecondsPadding ? '0:' : ''}${secondsStr}`;
    }
  }
}
